import {
  Component, ElementRef, EventEmitter, Input, Output, ViewChild,
} from '@angular/core';

import { ContentBlockModel } from 'src/app/@shared/content-block/@shared/content-block.model';

export type VideoType = 'vimeo' | 'youtube' | 'native';
export type VideoStatus = 'loading' | 'paused' | 'stopped' | 'playing' | 'error' | undefined;

interface WebkitHTMLVideoElement extends HTMLVideoElement {
  webkitSetPresentationMode: Function;
  webkitSupportsPresentationMode: Function;
}

@Component({
  selector: 'proto-video',
  standalone: false,
  styleUrls: ['./proto-video.component.scss'],
  templateUrl: './proto-video.component.html',
})
export class ProtoVideoComponent {
  @ViewChild('VideoContainer', {
    static: false,
  }) public videoContainer: ElementRef;

  @Input() public videoType: VideoType = 'youtube';
  @Input() public playsInline: boolean = true;
  @Input() public id: string;

  @Output() public open: EventEmitter<ContentBlockModel> = new EventEmitter<ContentBlockModel>();
  @Output() public videoStatusChange: EventEmitter<{
    id: string; status: VideoStatus; }> = new EventEmitter<{ id: string; status: VideoStatus }>();

  public videoUrl: string;
  public videoElement: WebkitHTMLVideoElement;
  public videoStatus: VideoStatus;

  private isFullScreen: boolean;

  // eslint-disable-next-line accessor-pairs
  @Input() set url(videoUrl: string) {
    if (!videoUrl) {
      return;
    }

    // wait one tick to have access to video container element
    requestAnimationFrame(() => {
      this.setVideo(videoUrl);
    });
  }

  // eslint-disable-next-line accessor-pairs
  @Input() set updateVideoStatus(status: VideoStatus) {
    if (!this.videoStatus || !status || this.videoType !== 'native') {
      return;
    }

    this.videoStatus = status;
    console.info(this.videoStatus);

    if (status === 'stopped') {
      this.resetVideo();
    }

    if (status === 'paused') {
      this.videoElement.pause();
    }

    this.resetPictureInPictureMode();
  }

  public startVideo(event: Event): void {
    event.stopPropagation();

    this.videoStatus = 'loading';
    this.videoElement.play().catch();
  }

  public onFullscreenStateChange(): void {
    // this.isFullScreen = document.fullscreenElement || document.webkitFullscreenElement;

    if (this.isFullScreen) {
      document.documentElement.classList.add('fullscreen');
      // !this.platform.is('ios') && this.screenOrientation.unlock();

      return;
    }

    document.documentElement.classList.remove('fullscreen');
    // !this.platform.is('ios') && this.screenOrientation.lock(this.screenOrientation.ORIENTATIONS.PORTRAIT).catch();
  }

  public onVideoError(): void {
    console.error('video error');
    this.videoUrl = '';
  }

  public onVideoPlaying(): void {
    console.info('video playing');
    this.videoStatus = 'playing';
    this.videoStatusChange.emit({
      id: this.id,
      status: this.videoStatus,
    });
  }

  public onVideoPaused(): void {
    console.info('video paused');
    this.videoStatus = 'paused';
    this.videoStatusChange.emit({
      id: this.id,
      status: this.videoStatus,
    });
  }

  private setVideo(url: string): void {
    if (this.videoType !== 'native') {
      this.videoUrl = url + this.getIframeVideoOptions(this.videoType);

      return;
    }

    if (!this.videoContainer || !this.videoContainer.nativeElement) {
      this.videoUrl = '';

      return;
    }

    this.videoUrl = url;
    this.videoElement = this.videoContainer.nativeElement.querySelector('.video-embed');
    this.videoElement.src = this.videoUrl;
    this.videoElement.load();
  }

  private resetVideo(): void {
    this.videoElement.pause();
    this.videoElement.currentTime = 0;
    this.videoElement.load();
    this.videoStatus = null;
  }

  private resetPictureInPictureMode(): void {
    if (this.videoElement.webkitSupportsPresentationMode
      && typeof this.videoElement.webkitSetPresentationMode === 'function') {
      this.videoElement.webkitSetPresentationMode('inline');
    }
  }

  private getIframeVideoOptions(videoType: VideoType): string {
    if (videoType === 'vimeo') {
      return `?portrait=0&byline=0&title=false&playsinline=${this.playsInline}`;
    }

    return `?showinfo=0&rel=0&iv_load_policy=3&disablekb=1&playsinline=${this.playsInline
      ? 1
      : 0}`;
  }
}
